import { Link } from "react-router-dom";
import {
  Car, Shield, DollarSign, CheckCircle2, ArrowRight,
  Wrench, Sparkles, TrendingUp, MapPin, Phone
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BRAND } from "../lib/constants";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const Stat = ({ value, label }) => (
  <div className="border-r border-white/15 last:border-0 px-6 py-4 md:py-0" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="font-display font-black text-3xl md:text-5xl tracking-tighter">{value}</div>
    <div className="gd-tag text-white/60 mt-1">{label}</div>
  </div>
);

const Marquee = () => {
  const items = ["UBER", "LYFT", "DOORDASH", "UBER EATS", "INSTACART", "GRUBHUB", "AMAZON FLEX", "SHIPT", "SPARK"];
  const repeated = [...items, ...items];
  return (
    <div className="bg-[#E60012] border-y-2 border-black overflow-hidden py-4">
      <div className="flex gd-marquee whitespace-nowrap">
        {repeated.map((it, i) => (
          <span key={i} className="font-display font-black text-2xl md:text-3xl text-white px-6 flex items-center gap-6">
            {it} <span className="text-black">●</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const HowStep = ({ num, title, desc, icon: Icon }) => (
  <div className="relative bg-white border-2 border-black p-6 md:p-8" data-testid={`step-${num}`}>
    <div className="absolute -top-4 -left-1 bg-[#E60012] text-white font-display font-black text-3xl px-4 py-1 border-2 border-black">
      {String(num).padStart(2, "0")}
    </div>
    <div className="mt-4">
      <Icon size={28} strokeWidth={2.5} className="mb-3" />
      <h3 className="font-display font-black text-xl mb-2 tracking-tight uppercase">{title}</h3>
      <p className="text-black/65 leading-relaxed text-sm">{desc}</p>
    </div>
  </div>
);

const Benefit = ({ icon: Icon, title, desc }) => (
  <div className="flex gap-4 p-5 border border-white/10 hover:border-[#E60012] gd-snappy" data-testid={`benefit-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="shrink-0 w-12 h-12 bg-[#E60012] flex items-center justify-center">
      <Icon size={22} strokeWidth={2.5} className="text-white" />
    </div>
    <div>
      <h4 className="font-display font-bold text-lg mb-1">{title}</h4>
      <p className="text-white/60 text-sm leading-relaxed">{desc}</p>
    </div>
  </div>
);

export default function LandingPage() {
  return (
    <div className="bg-white text-black">
      <Navbar />

      {/* HERO */}
      <section
        className="relative bg-[#0A0A0A] text-white overflow-hidden"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 opacity-30">
          <img src={BRAND.heroBg} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent"></div>
        <div className="absolute inset-0 gd-stripes-dark"></div>

        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-16 pb-20 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="gd-tag text-[#E60012] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[#E60012]"></span>
              Built for gig drivers · est. 2026
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter uppercase leading-[0.9] mb-6">
              Drive<br />
              what <span className="text-[#E60012]">earns</span>.<br />
              Skip the<br />
              dealership.
            </h1>
            <p className="text-white/70 text-base md:text-lg max-w-xl mb-8 leading-relaxed">
              We hand-pick a rideshare-approved car for every driver and put it in your hands within days. Insurance included. Unlimited miles. No credit hassle, no long contracts. Just the keys and the road.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/apply"
                className="bg-[#E60012] text-white font-display font-black uppercase tracking-wider px-8 py-5 text-base inline-flex items-center justify-center gap-3 border-2 border-[#E60012] hover:bg-white hover:text-black hover:border-white gd-snappy"
                data-testid="hero-apply-btn"
              >
                Apply in 3 minutes <ArrowRight size={18} />
              </Link>
              <a
                href={`tel:${BRAND.phone}`}
                className="bg-transparent text-white font-display font-bold uppercase tracking-wider px-8 py-5 text-base inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white gd-snappy"
                data-testid="hero-call-btn"
              >
                <Phone size={16} /> Talk to a human
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/55">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#E60012]" />
                <span className="font-mono">Insurance included</span>
              </span>
              <span className="opacity-30 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#E60012]" />
                <span className="font-mono">No credit check</span>
              </span>
              <span className="opacity-30 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#E60012]" />
                <span className="font-mono">Locally owned</span>
              </span>
            </div>
          </div>

          {/* Side card */}
          <div className="md:col-span-4 md:mt-12">
            <div className="bg-[#E60012] text-white border-2 border-white p-6 md:p-7 md:rotate-1">
              <div className="gd-tag mb-3">Weekly rate</div>
              <div className="font-display font-black text-5xl tracking-tighter mb-1">$350</div>
              <div className="text-sm mb-4 text-white/90">/week — all in</div>
              <p className="text-sm text-white/85 mb-4 leading-relaxed">
                Insurance, maintenance, and unlimited miles included. No hidden fees, no surprise charges.
              </p>
              <Link
                to="/apply"
                className="block w-full text-center bg-black text-white font-display font-bold uppercase tracking-wider px-4 py-3 border-2 border-black hover:bg-white hover:text-black gd-snappy"
                data-testid="hero-special-btn"
              >
                Apply for a car
              </Link>
              <p className="font-mono text-[10px] text-white/60 mt-3">Subject to approval.</p>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="relative border-t border-white/15 bg-black/40">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/15">
            <Stat value="$0" label="Down payment" />
            <Stat value="∞" label="Miles included" />
            <Stat value="100%" label="Rideshare approved" />
            <Stat value="24/7" label="Driver support" />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* HOW IT WORKS */}
      <section
        id="how"
        className="relative py-20 md:py-28 bg-[#0A0A0A] text-white overflow-hidden"
        data-testid="how-section"
      >
        <div className="absolute inset-0 opacity-20">
          <img src={BRAND.textureBg} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="gd-tag text-[#E60012] mb-3">▌ How it works</div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase leading-[0.95]">
              From application<br />to <span className="text-[#E60012]">income</span>, fast.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <HowStep num={1} title="Apply Online" icon={Sparkles}
              desc="Fill out a 3-minute application — license details, ID photos, your gig platforms. That's it." />
            <HowStep num={2} title="We Match You" icon={CheckCircle2}
              desc="We review your application and hand-pick the right rideshare-approved car for your hustle. Then we call you back." />
            <HowStep num={3} title="Pick Up & Earn" icon={TrendingUp}
              desc="Walk in, sign, drive out. Insurance active, app-ready, miles unlimited. Start earning the same week." />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="why" className="py-20 md:py-28 bg-[#0A0A0A] text-white border-t border-white/10" data-testid="benefits-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5 md:sticky md:top-28">
            <div className="gd-tag text-[#E60012] mb-3">▌ Why GigDrive</div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase leading-[0.95] mb-6">
              Built for<br />drivers who<br />don't quit.
            </h2>
            <p className="text-white/60 leading-relaxed mb-8">
              Traditional rental companies don't get the gig grind. We do. Every detail of GigDrive is engineered for one thing: keeping you on the road and earning.
            </p>
            <Link
              to="/apply"
              className="bg-[#E60012] text-white font-display font-black uppercase tracking-wider px-8 py-4 inline-flex items-center gap-3 border-2 border-[#E60012] hover:bg-white hover:text-black hover:border-white gd-snappy"
              data-testid="benefits-apply-btn"
            >
              Start your application <ArrowRight size={18} />
            </Link>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            <Benefit icon={Shield} title="Insurance Included"
              desc="Full commercial-grade rideshare insurance. No hidden fees, no separate policy required." />
            <Benefit icon={CheckCircle2} title="Unlimited Miles"
              desc="Drive 200 miles or 2,000 miles a week — your rate stays the same. Always." />
            <Benefit icon={Wrench} title="Free Maintenance"
              desc="Oil changes, tire rotations, brake service. We handle it. You just keep driving." />
            <Benefit icon={DollarSign} title="No Credit Check Hurdles"
              desc="We weigh gig earnings, not credit scores. Most drivers approved regardless of FICO." />
            <Benefit icon={Car} title="Rideshare-Approved"
              desc="Every car pre-vetted for Uber, Lyft, and delivery platforms. Zero deactivation risk." />
            <Benefit icon={MapPin} title="Local Pickup"
              desc="Multiple pickup locations across the metro. No airport runs, no waiting in line." />
          </div>
        </div>
      </section>

      {/* FOUNDER PROMISE */}
      <section className="py-16 md:py-20 bg-[#E60012] text-white border-y-2 border-black" data-testid="promise-section">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <div className="gd-tag text-white/80 mb-4">▌ Our promise</div>
          <p className="font-display font-black text-2xl md:text-4xl tracking-tight leading-tight mb-6">
            "We started GigDrive because gig drivers deserve better than dealership runarounds. Every car we hand over is one we'd be proud to drive ourselves — and if it isn't right, we'll swap it. Period."
          </p>
          <div className="font-mono text-sm">
            THE GIGDRIVE TEAM · LOCALLY OWNED · EST. 2026
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28" data-testid="faq-section">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <div className="gd-tag text-[#E60012] mb-3">▌ FAQ</div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase">Questions, answered.</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {[
              { q: "What do I need to apply?", a: "A valid driver's license (1+ year), clear photos of your license front and back, and basic personal info. No FICO requirements, no proof-of-income gymnastics." },
              { q: "Do I get to pick the car?", a: "We pick the right rideshare-approved car for you based on your hustle (rideshare vs delivery), your weekly hours, and what we have available. You can tell us your preference and we'll do our best to match it." },
              { q: "How fast can I get a car?", a: "We're a new, hands-on team — we typically get back to you the same day, and most drivers are on the road within a week." },
              { q: "Is insurance really included?", a: "Yes. Full commercial rideshare-grade insurance comes with every rental. You don't need a separate policy or rideshare endorsement." },
              { q: "Are there mileage limits?", a: "Zero. Drive 100 miles a week or 3,000 — your rate doesn't change. Unlimited means unlimited." },
              { q: "What if I have an accident or need maintenance?", a: "Call our driver support line. We help with the claim and work to get you back behind the wheel as fast as possible." },
              { q: "Can I rent if I drive only delivery (no rideshare)?", a: "Absolutely. DoorDash, Instacart, Amazon Flex, Spark — you name it. We've got cars perfect for delivery." },
              { q: "Is there a deposit?", a: "Most drivers start with $0 down. Some plans may require a small refundable deposit depending on rental duration and driving history." },
            ].map((it, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-2 border-black bg-white px-5"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-display font-bold text-lg text-left hover:no-underline">
                  {it.q}
                </AccordionTrigger>
                <AccordionContent className="text-black/70 leading-relaxed">
                  {it.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 gd-stripes-dark"></div>
        <div className="relative max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <h2 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter uppercase leading-[0.9] mb-6">
            Ready to<br /><span className="text-[#E60012]">drive smarter?</span>
          </h2>
          <p className="text-white/60 text-lg max-w-xl mx-auto mb-10">
            Three minutes. Zero pressure. We'll get back to you the same day.
          </p>
          <Link
            to="/apply"
            className="bg-[#E60012] text-white font-display font-black uppercase tracking-wider px-12 py-6 text-lg inline-flex items-center gap-3 border-2 border-[#E60012] hover:bg-white hover:text-black hover:border-white gd-snappy"
            data-testid="cta-apply-btn"
          >
            Start application <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
